import { get } from "lodash";
import { useEffect } from "react";
import { trackPromise } from "react-promise-tracker";

import { Button, cn } from "@shared/ui";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import ReportLink from "../components/report/ReportLink";
import { useReport, useReportArea } from "../components/report/ReportProvider";
import {
  AXLE_ERROR_SUBHEADER,
  CARRIER_ERROR_SUBHEADER,
  CONNECTION_ERROR_SUBHEADER,
} from "../constants";
import { updateIgnition } from "../lib/axle";
import {
  generateLinkErrorMessage,
  generateManualErrorMessage,
  isIntegrationV123,
} from "../lib/utility";

// A reload resets app state, so the one allowed reload is remembered for the tab.
const RELOAD_KEY = "ignition-error-reloaded";

const hasReloaded = () => {
  try {
    return sessionStorage.getItem(RELOAD_KEY) === "true";
  } catch {
    return false;
  }
};

const reloadPage = () => {
  try {
    sessionStorage.setItem(RELOAD_KEY, "true");
  } catch {
    // Without storage the next error reloads again instead of failing.
  }
  window.location.reload();
};

const Error = ({
  client,
  session,
  nextStep,
  step,
  previousStep,
  loginInformation,
  linkError,
  manualError,
  posthog,
}) => {
  // With the report link showing, the layout is tighter so the screen fits without scrolling.
  const reportArea = useReportArea(step);
  const reporting = useReport();
  const isV123Integration = isIntegrationV123(loginInformation);

  useEffect(() => {
    // Coming back from the report page is not a new error.
    if (reporting?.returnedFromReport) return;
    posthog.capture("$pageview", {
      step,
      carrier: get(loginInformation, "carrier.id"),
    });
    // Updates ignition with error data on carrier-error condition
    const fail = async () => {
      let errorMessage = null;
      if (step === "carrier-error") {
        errorMessage = generateLinkErrorMessage(linkError);
      } else if (step === "manual-document-error") {
        errorMessage = generateManualErrorMessage(manualError);
      } else if (step === "mfa-options-error") {
        errorMessage = linkError;
      }
      // Construct webhook event
      const lastEvent = {
        type: `ignition.errored`,
        data: {
          token: session.id,
          message: errorMessage,
          user: session.user,
          client: client.id,
        },
        createdAt: new Date().toISOString(),
      };
      const params = {
        status: "errored",
        lastEvent,
      };

      const [updateIgnitionResponse, updateIgnitionError] =
        await updateIgnition(session.id, params);

      if (updateIgnitionError) {
        switch (get(updateIgnitionError, "code")) {
          case 500:
            console.log(JSON.stringify(updateIgnitionError));
            nextStep("axle-error");
            return;
          default:
            nextStep("connection-error");
            return;
        }
      }
    };

    if (step !== "connection-error") {
      // Scoped so this background webhook call never blocks App.js's
      // app-wide loader if it's slow — the user is already looking at
      // this error screen, so there's nothing to visually "load" here.
      trackPromise(fail(), "error-step");
    }
    // Once per error screen; session and login updates must not resend the webhook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const error = {
    "carrier-error": {
      illustration: "manual-required",
      header: "Hmm. Something’s not right.",
      subheader: CARRIER_ERROR_SUBHEADER,
      primary: {
        text: get(session, "config.carrier-error.primary.text") ?? "Continue",
        step: get(session, "config.manual.enabled")
          ? () => nextStep("manual-account")
          : () => nextStep("failed"),
      },
      secondary: {
        text:
          get(session, "config.carrier-error.secondary.text") ?? "Try again",
        // If integration type is V123, route to carrier selection page instead of previous step
        // [without V123] Otherwise, if previous step was enterMfa, go back to login; otherwise return to previous step
        step:
          isV123Integration
            ? () => nextStep("carriers")
            : previousStep === "enterMfa"
            ? () => nextStep("login")
            : () => nextStep(previousStep),
      },
    },
    "mfa-options-error": {
      illustration: "manual-required",
      header: "Hmm. Something's not right.",
      subheader: linkError,
      primary: {
        text: "Try again",
        // If integration type is V123, route to carrier selection page instead of previous step
        // [without V123] Otherwise, if previous step was enterMfa, go back to login; otherwise return to previous step
        step:
          isV123Integration
            ? () => nextStep("carriers")
            : previousStep === "enterMfa"
            ? () => nextStep("login")
            : () => nextStep(previousStep),
      },
      secondary: {
        text: "Continue",
        step: get(session, "config.manual.enabled")
          ? () => nextStep("manual-account")
          : () => nextStep("failed"),
      },
    },
    "manual-document-error": {
      illustration: "manual-required",
      header: "Hmm. Something’s not right.",
      subheader: "We're unable to process your document right now.",
      primary: {
        text: "Try again",
        step: () => nextStep(previousStep),
      },
      secondary: {
        text: "Continue",
        step: () => nextStep("failed"),
      },
    },
    "connection-error": {
      illustration: "manual-required",
      header: "Hmm. Something’s not right.",
      subheader: CONNECTION_ERROR_SUBHEADER,
      primary: {
        text: "Refresh page",
        step: () => window.location.reload(),
      },
    },
    "axle-error": {
      illustration: "manual-required",
      header: "Hmm. Something’s not right.",
      subheader: AXLE_ERROR_SUBHEADER,
      primary: {
        text: "Refresh page",
        step: () => (hasReloaded() ? nextStep("failed") : reloadPage()),
      },
    },
  };

  return (
    <>
      <StepBody
        className={cn(
          "items-center text-center",
          reportArea ? "grow gap-0 pb-4 pt-0" : "gap-4"
        )}
      >
        <div
          role="img"
          style={{
            backgroundImage: `url("./icons/${error[step].illustration}.svg")`,
          }}
          className={cn(
            !reportArea && "mt-8",
            "h-32 w-full flex-none bg-contain bg-center bg-no-repeat"
          )}
          aria-label="Error"
        ></div>
        <StepHeading
          align="center"
          title={error[step].header}
          description={error[step].subheader}
        />
      </StepBody>
      <StepFooter>
        {error[step].primary && (
          <Button
            onClick={error[step].primary.step}
          >
            {error[step].primary.text}
          </Button>
        )}
        {error[step].secondary && (
          <Button
            variant="secondary"
            type="button"
            onClick={error[step].secondary.step}
          >
            {error[step].secondary.text}
          </Button>
        )}
        {reportArea && <ReportLink area={reportArea} step={step} />}
      </StepFooter>
    </>
  );
};

export default Error;
